import type {
  NFTData,
  NFTAttribute,
  NFTCollection,
  NFTCreator,
  NFTRoyalty,
  NFTAuthority,
  NFTOwnership,
  NFTSupply,
} from '../../types';
import type { HeliusAssetResponse } from './heliusApi';

function extractImageUrl(asset: HeliusAssetResponse): string {
  if (asset.content.links?.image) {
    return asset.content.links.image;
  }

  const imageFile = asset.content.files?.find(
    (file) => file.mime?.startsWith('image/') || file.uri?.match(/\.(png|jpg|jpeg|gif|webp)$/i)
  );

  if (imageFile) {
    return imageFile.uri;
  }

  return '';
}

function extractCollection(asset: HeliusAssetResponse): NFTCollection | null {
  const collectionGroup = asset.grouping?.find((g) => g.group_key === 'collection');

  if (!collectionGroup) {
    return null;
  }

  return {
    address: collectionGroup.group_value,
    name: collectionGroup.collection_metadata?.name || 'Unknown Collection',
    verified: collectionGroup.verified ?? false,
  };
}

function extractAttributes(asset: HeliusAssetResponse): NFTAttribute[] {
  const attrs = asset.content.metadata.attributes;

  if (!attrs || !Array.isArray(attrs)) {
    return [];
  }

  return attrs.map((attr) => ({
    traitType: attr.trait_type || 'Unknown',
    value: attr.value ?? '',
  }));
}

function extractCreators(asset: HeliusAssetResponse): NFTCreator[] {
  if (!asset.creators || !Array.isArray(asset.creators)) {
    return [];
  }

  return asset.creators.map((creator) => ({
    address: creator.address,
    share: creator.share,
    verified: creator.verified,
  }));
}

function extractRoyalty(asset: HeliusAssetResponse): NFTRoyalty {
  return {
    royaltyModel: asset.royalty.royalty_model,
    target: asset.royalty.target,
    percent: asset.royalty.percent,
    basisPoints: asset.royalty.basis_points,
    primarySaleHappened: asset.royalty.primary_sale_happened,
    locked: asset.royalty.locked,
  };
}

function extractAuthorities(asset: HeliusAssetResponse): NFTAuthority[] {
  if (!asset.authorities || !Array.isArray(asset.authorities)) {
    return [];
  }

  return asset.authorities.map((auth) => ({
    address: auth.address,
    scopes: auth.scopes,
  }));
}

function extractOwnership(asset: HeliusAssetResponse): NFTOwnership {
  return {
    owner: asset.ownership.owner,
    delegate: asset.ownership.delegate,
    delegated: asset.ownership.delegated,
    ownershipModel: asset.ownership.ownership_model,
  };
}

function extractSupply(asset: HeliusAssetResponse): NFTSupply | undefined {
  if (!asset.supply) {
    return undefined;
  }

  return {
    printMaxSupply: asset.supply.print_max_supply,
    printCurrentSupply: asset.supply.print_current_supply,
    editionNonce: asset.supply.edition_nonce,
  };
}

export function parseHeliusAsset(asset: HeliusAssetResponse): NFTData {
  const metadata = asset.content.metadata;

  return {
    mintAddress: asset.id,
    name: metadata.name || 'Unnamed NFT',
    symbol: metadata.symbol || '',
    description: metadata.description || '',
    imageUrl: extractImageUrl(asset),
    collection: extractCollection(asset),
    attributes: extractAttributes(asset),
    owner: asset.ownership.owner,
    // Extended metadata
    ownership: extractOwnership(asset),
    royalty: extractRoyalty(asset),
    creators: extractCreators(asset),
    authorities: extractAuthorities(asset),
    mutable: asset.mutable,
    burnt: asset.burnt,
    supply: extractSupply(asset),
    jsonUri: asset.content.json_uri,
    externalUrl: asset.content.links?.external_url,
    layerAssets: [],
  };
}
